<template>
    <div class="tile-content tile-content-secondary">
        <div class="position-relative">
            <v-inline-loader secondary v-if="isLoading"/>
            <div class="stack-m">
                <div class="grid">
                    <div class="grid-item grid-item-full">
                        <p>When a transaction meets this rules conditions, this action controls which gateway account
                            will
                            be selected according to the weighted distributions set below.</p>
                    </div>
                    <div class="grid-item grid-item-half">
                        <v-radio
                            class="stack-m"
                            group="eventsFilter"
                            label="Use gateway account weights"
                            :value="pickMethods.gatewayAccountWeights"
                            :model="instructionMethod"
                            @update="updateInstructionMethod"
                        />
                        <v-radio
                            group="eventsFilter"
                            label="Use acquirer weights"
                            :value="pickMethods.gatewayAcquirerWeights"
                            :model="instructionMethod"
                            @update="updateInstructionMethod"
                        />
                    </div>
                </div>
            </div>
            <div v-if="!isLoading && weighingOptions.length">
                <table class="table">
                    <colgroup>
                        <col style="width:100px">
                        <col>
                    </colgroup>
                    <tbody>
                    <tr
                        v-for="(option, optionIndex) in weighingOptions"
                        :key="optionIndex">
                        <td>
                            <r-input
                                :value="option.weight"
                                :validate="$v.weighingOptions.$each.$iter[optionIndex]"
                                @input="updateWeight(option, optionIndex, $event)"/>
                        </td>
                        <td>
                            <div class="padding-bottom-xxs">{{option.label}}</div>
                            <item-weight-bar :relative-weight="getRelativeWeight(option)"/>
                        </td>
                    </tr>
                    </tbody>
                </table>
            </div>
            <div v-else>
                <p>You have not defined any gateway accounts.</p>
            </div>
        </div>
    </div>
</template>

<script>
    import {
        GatewayAccountPickInstructionModel,
        GatewayAccountWeightModel,
        GatewayAcquirerWeightModel,
    } from '@/common/models/rules/actions/pick-gateway-account-action-model';
    import {vRadio} from '@/components/form';
    import vInlineLoader from '@/components/v-inline-loader';
    import {numeric, minValue, maxValue} from 'vuelidate/lib/validators';
    import _ from '@/common/helpers';
    import BaseAction from '../base-action';
    import ItemWeightBar from '../shared/item-weight-bar';

    export default {
        name: 'pick-gateway-account',
        extends: BaseAction,
        components: {
            vRadio, vInlineLoader, ItemWeightBar,
        },
        computed: {
            instructionMethod() {
                return this.action.pickInstruction.method
                    || GatewayAccountPickInstructionModel.Methods.gatewayAccountWeights;
            },
            weighingOptions() {
                const {gateways} = this.$store.getters['rulesEngine/gateways'];
                if (this.instructionMethod
                    === GatewayAccountPickInstructionModel.Methods.gatewayAccountWeights) {
                    return gateways.map(({fields}) => new GatewayAccountWeightModel({
                        gatewayAccountId: fields.id,
                        weight: this.getWeightByAccount(fields.id),
                    }));
                }
                // generate acquirer options
                return Object
                    .values(gateways
                        .reduce((result, {fields}) => {
                            const weight = new GatewayAcquirerWeightModel({
                                gatewayName: fields.gatewayName,
                                acquirerName: fields.acquirerName,
                                weight: this.getWeightByAcquirer(
                                    fields.gatewayName,
                                    fields.acquirerName,
                                ),
                            });
                            result[weight.label] = weight;
                            return result;
                        }, {}))
                    .sort((a, b) => {
                        // alphabetical sort by their label
                        if (a.label < b.label) {
                            return -1;
                        }
                        if (a.label > b.label) {
                            return 1;
                        }
                        return 0;
                    });
            },
            totalWeight() {
                return this.weighingOptions.reduce((total, {weight}) => {
                    total += isNaN(Number(weight)) ? 0 : Number(weight);
                    return total;
                }, 0);
            },
        },
        data() {
            return {
                pickMethods: {...GatewayAccountPickInstructionModel.Methods},
                isLoading: true,
                cachedWeights: {
                    [GatewayAccountPickInstructionModel.Methods.gatewayAccountWeights]: null,
                    [GatewayAccountPickInstructionModel.Methods.gatewayAcquirerWeights]: null,
                },
            };
        },
        validations: {
            weighingOptions: {
                $each: {
                    weight: {
                        numeric,
                        minValue: minValue(0),
                        maxValue: maxValue(Number.MAX_SAFE_INTEGER),
                    },
                },
            },
        },
        methods: {
            /**
             * Triggered when the radio buttons are used to switch the instruction
             * method.
             * @param {GatewayAccountPickInstructionModel.Methods} method
             */
            updateInstructionMethod(method) {
                if (this.action.pickInstruction.weightedList.length) {
                    const target = method
                        === GatewayAccountPickInstructionModel.Methods.gatewayAccountWeights
                        ? GatewayAccountPickInstructionModel.Methods.gatewayAcquirerWeights
                        : GatewayAccountPickInstructionModel.Methods.gatewayAccountWeights;
                    // cache the current weights before switching to the new ones
                    this.cachedWeights[target] = new GatewayAccountPickInstructionModel(this.action
                        .pickInstruction.getPayload());
                }
                if (this.cachedWeights[method] !== null) {
                    // restore cached weights if present
                    this.action.pickInstruction = this.cachedWeights[method];
                } else {
                    // otherwise initialize a default instruction
                    this.action.pickInstruction = new GatewayAccountPickInstructionModel({method});
                }
                const clone = this.action.cloneSelf();
                clone.pickInstruction.method = method;
                this.$emit('input', clone);
            },
            /**
             * Update the local cached weight and trigger
             * validation.
             * @param {Object} option
             * @param {number} index
             * @param {string} value
             */
            updateWeight(option, index, value) {
                const weight = Number(value);
                if (!isNaN(weight)) {
                    option.weight = weight;
                    this.updateWeightedList();
                } else {
                    option.weight = value;
                }
                this.$v.$touch();
            },
            /**
             * Trigger an update upwards for the weighted list
             * generated from the options displayed where the weight
             * is greater than zero.
             */
            updateWeightedList() {
                this.action.pickInstruction.weightedList = this.weighingOptions
                    .filter(({weight}) => !_.isBlank(weight) && weight > 0);
                this.$emit('input', this.action.cloneSelf());
            },
            /**
             * Calculate the relative weight of the option.
             * Returns a percentage.
             * @param {string|number} weight
             * @returns {number}
             */
            getRelativeWeight({weight}) {
                if (this.totalWeight === 0 || isNaN(weight)) {
                    return 0;
                }
                return Math.round((weight / this.totalWeight) * 100);
            },
            /**
             * Used for the list computation to match the option
             * with the weight found in the model.
             * @param {string} gatewayAccountId
             * @returns {number}
             */
            getWeightByAccount(gatewayAccountId) {
                const match = this.action.pickInstruction.weightedList
                    .find(item => item.gatewayAccountId === gatewayAccountId);
                if (match) {
                    return match.weight;
                }
                return 0;
            },
            /**
             * Used for the list computation to match the option
             * with the weight found in the model.
             * @param {string} gatewayName
             * @param {string} acquirerName
             * @returns {number}
             */
            getWeightByAcquirer(gatewayName, acquirerName) {
                const option = new GatewayAcquirerWeightModel({gatewayName, acquirerName});
                const match = this.action.pickInstruction.weightedList
                    .find(item => item.label === option.label);
                if (match) {
                    return match.weight;
                }
                return 0;
            },
            getValidationAggregate() {
                return {
                    name: 'pick-gateway-account',
                    rules: [
                        {
                            name: `The ${_.unKebabCase(this.action.pickInstruction.method, false)} must be numerical and greater than or equal to zero (0).`,
                            delegate: () => {
                                this.$v.$touch();
                                return this.$v.$error;
                            },
                        },
                        {
                            name: `You have no gateway accounts to use for the "Pick Gateway Account" action. Please remove the action or add gateway accounts.`,
                            delegate: () => this.weighingOptions.length === 0,
                        },
                    ],
                };
            },
        },
        created() {
            (async () => {
                await this.$store.dispatch('rulesEngine/fetchGatewayAccounts');
                this.isLoading = false;
            })();
        },
    };
</script>
